import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { TiWarning } from 'react-icons/ti';
import { useParams } from 'react-router-dom';

import { useGetSpacesByIdQuery, useGetStatisticsVacancyQuery } from '@client/api';
import { Card, CardContent, CardHeader, CardTitle, isDefinedNumber, Text, toNumberString } from '@client/toolkit';

import { AreaWidget } from '../area-widget/area-widget.component';

export type GroupItemsCardProps = {
  className?: string;
  dense?: boolean;
};

export function GroupItemsCard({ className, dense = false }: GroupItemsCardProps) {
  const { t: tType } = useTranslation('type');
  const { t, ready } = useTranslation('rentalObjects');
  const { t: tGlobal } = useTranslation('global');

  const { spaceId } = useParams<{ spaceId: string }>();

  const { data: space, isFetching } = useGetSpacesByIdQuery({ id: spaceId ?? '' }, { skip: !spaceId });

  const ancestorPath = useMemo(() => space?.path, [space?.path]);

  // Fetch CommercialSpace only for vacancy (Ledig/Utleid)
  const { data: vacancyReport, isFetching: isFetchingVacancy } = useGetStatisticsVacancyQuery(
    {
      'ancestorPath.Equal': ancestorPath,
      'discriminator.Contains': ['CommercialSpace'],
    },
    { skip: !ancestorPath },
  );

  // Fetch ALL spaces for over/under-allocated calculation
  const { data: allSpacesReport, isFetching: isFetchingAllSpaces } = useGetStatisticsVacancyQuery(
    {
      'ancestorPath.Equal': ancestorPath,
    },
    { skip: !ancestorPath },
  );

  const { data: commonAreaReport, isFetching: isFetchingCommonArea } = useGetStatisticsVacancyQuery(
    {
      'ancestorPath.Equal': ancestorPath,
      'discriminator.Contains': ['CommonArea'],
    },
    { skip: !ancestorPath },
  );

  const isLoading = isFetching || isFetchingVacancy || isFetchingAllSpaces || isFetchingCommonArea;

  // Use the group's defined area (from the Areal field) as the total
  const definedArea = space?.area?.metric;
  // Use ALL child spaces for over/under-allocated calculation
  const childSpacesArea = allSpacesReport?.total?.area?.metric;
  
  // Calculate extra area if child spaces exceed the defined area
  const extraArea = useMemo(() => {
    if (!isDefinedNumber(definedArea) || !isDefinedNumber(childSpacesArea)) return undefined;
    const extra = childSpacesArea - definedArea;
    return extra > 0 ? extra : undefined;
  }, [definedArea, childSpacesArea]);

  // Calculate unused area if defined area exceeds child spaces
  const unusedArea = useMemo(() => {
    if (!isDefinedNumber(definedArea) || !isDefinedNumber(childSpacesArea)) return undefined;
    const unused = definedArea - childSpacesArea;
    return unused > 0 ? unused : undefined;
  }, [definedArea, childSpacesArea]);

  const isAreasExceeded = isDefinedNumber(extraArea) && extraArea > 0;
  const isAreasUnderAllocated = isDefinedNumber(unusedArea) && unusedArea > 0;

  // Display the defined area if set, otherwise fall back to child spaces total
  const displayTotalArea = definedArea ?? childSpacesArea;

  const vacantArea = vacancyReport?.vacant?.area?.metric;
  const rentedArea = vacancyReport?.rented?.area?.metric;
  const commonAreaTotal = commonAreaReport?.total?.area?.metric;

  return (
    <Card className={className}>
      <CardHeader>
        <div className="flex-1">
          <CardTitle className="flex-1" variant="dense">
            {tGlobal('entity.commercialSpace', { context: 'plural' })}
          </CardTitle>
          {!isLoading && isAreasExceeded && (
            <span className="text-brand-primary text-xs block mt-2 -mb-1">
              <TiWarning className="text-lg w-4 h-4 -mt-1 inline-block mr-1" />
              <Text loading={!ready}>{t('group.totalAreaExceeded')}</Text>
            </span>
          )}
          {!isLoading && isAreasUnderAllocated && (
            <span className="text-[#a1de6c] text-xs block mt-2 -mb-1">
              <TiWarning className="text-lg w-4 h-4 -mt-1 inline-block mr-1" />
              <Text loading={!ready}>{t('group.totalAreaUnderAllocated')}</Text>
            </span>
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 gap-2 md:grid-cols-4">
          <AreaWidget
            label={tGlobal('total')}
            total={
              <span className="inline-flex items-baseline gap-1 flex-wrap">
                <Text
                  className="text-white whitespace-nowrap"
                  loading={isLoading}
                  isEmpty={!isDefinedNumber(displayTotalArea)}
                >
                  {toNumberString(displayTotalArea ?? undefined, undefined, {
                    unit: tType('AreaMeasurementUnit.SquareMeters', { context: 'short' }),
                  })}
                </Text>
                {isAreasExceeded && (
                  <span
                    className="text-brand-primary font-medium whitespace-nowrap"
                    style={{ fontSize: '0.65rem' }}
                  >
                    (+{' '}
                    {toNumberString(extraArea, undefined, {
                      unit: tType('AreaMeasurementUnit.SquareMeters', { context: 'short' }),
                    })}
                    )
                  </span>
                )}
                {isAreasUnderAllocated && (
                  <span
                    className="text-[#a1de6c] font-medium whitespace-nowrap"
                    style={{ fontSize: '0.65rem' }}
                  >
                    (-{' '}
                    {toNumberString(unusedArea, undefined, {
                      unit: tType('AreaMeasurementUnit.SquareMeters', { context: 'short' }),
                    })}
                    )
                  </span>
                )}
              </span>
            }
            dense={dense}
          />

          <AreaWidget
            label={tGlobal('vacant')}
            subtitle={tType('SpaceDiscriminator.CommercialSpace').toLowerCase()}
            total={
              <Text
                className="text-white"
                loading={isLoading}
                isEmpty={!isDefinedNumber(vacantArea)}
              >
                {toNumberString(vacantArea ?? undefined, undefined, {
                  unit: tType('AreaMeasurementUnit.SquareMeters', { context: 'short' }),
                })}
              </Text>
            }
            dense={dense}
          />

          <AreaWidget
            label={tGlobal('rented')}
            subtitle={tType('SpaceDiscriminator.CommercialSpace').toLowerCase()}
            total={
              <Text
                className="text-white"
                loading={isLoading}
                isEmpty={!isDefinedNumber(rentedArea)}
              >
                {toNumberString(rentedArea ?? undefined, undefined, {
                  unit: tType('AreaMeasurementUnit.SquareMeters', { context: 'short' }),
                })}
              </Text>
            }
            dense={dense}
          />

          <AreaWidget
            label={tGlobal('commonAreaTotal')}
            total={
              <Text
                className="text-white"
                loading={isLoading}
                isEmpty={!isDefinedNumber(commonAreaTotal)}
              >
                {toNumberString(commonAreaTotal ?? undefined, undefined, {
                  unit: tType('AreaMeasurementUnit.SquareMeters', { context: 'short' }),
                })}
              </Text>
            }
            dense={dense}
          />
        </div>
      </CardContent>
    </Card>
  );
}
